import { useMemo } from 'react';
import { FlexColumn } from '@elementary/common-ui/components';
import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { RuleFormFields } from 'shared/types';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';
import { RuleAsset } from '../../types';
import RuleFilterSelector from './RuleFilterSelector';
import RuleFilterChips from './RuleFilterChips';

const defaultOperators: Operator[] = ['equals', 'notEquals', 'contains'];

const FilterTypeOptions = [
  { value: 'source', label: 'Source' },
  { value: 'model', label: 'Model' },
  { value: 'snapshot', label: 'Snapshot' },
  { value: 'seed', label: 'Seed' },
  { value: 'exposure', label: 'Exposure' },
];

enum FilterTypes {
  TAGS = 'tag',
  OWNERS = 'owner',
  NAMES = 'name',
  PATH = 'path',
  TYPE = 'type',
  IS_CRITICAL = 'is_critical',
}

type Props = {
  methods: UseFormReturn<RuleFormFields>;
  assets?: RuleAsset[];
  paths?: string[];
  metadataData?: { tags: Array<string>; owners: Array<string> };
  descriptionText?: string;
  disabled?: boolean;
};

const ImpactedAssetsFiltersFormContent = ({
  methods,
  assets,
  paths,
  metadataData,
  descriptionText = 'Receive alerts only if one or more of the selected assets are impacted by the incident.',
  disabled,
}: Props) => {
  const filterOptions = useMemo(() => {
    const boolOperators: Operator[] = ['equals', 'notEquals'];
    return [
      {
        property: FilterTypes.TAGS,
        propertyLabel: 'Tag',
        options: (metadataData?.tags || []).map((tag) => ({
          label: tag,
          value: tag,
        })),
        multiselect: true,
      },
      {
        property: FilterTypes.OWNERS,
        propertyLabel: 'Owner',
        options: (metadataData?.owners || []).map((owner) => ({
          label: owner,
          value: owner,
        })),
        multiselect: true,
      },
      {
        property: FilterTypes.NAMES,
        propertyLabel: 'Asset Name',
        options: distinctArray((assets || []).map((asset) => asset.name)).map(
          (name) => ({
            label: name,
            value: name,
          }),
        ),
        multiselect: true,
        isSingularValue: true,
      },
      {
        property: FilterTypes.PATH,
        propertyLabel: 'Path',
        options: (paths || []).map((path) => ({
          label: path,
          value: path,
        })),
        multiselect: true,
        isSingularValue: true,
      },
      {
        property: FilterTypes.TYPE,
        propertyLabel: 'Asset Type',
        options: FilterTypeOptions,
        multiselect: true,
        isSingularValue: true,
        showSearch: false,
      },
      {
        property: FilterTypes.IS_CRITICAL,
        propertyLabel: 'Critical asset',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
        operators: boolOperators,
        isSingularValue: true,
        showSearch: false,
        multiselect: false,
      },
    ].filter((o) => o.options.length > 0);
  }, [assets, paths, metadataData?.owners, metadataData?.tags]);

  return (
    <>
      <FlexColumn sx={{ gap: 0.5 }}>
        <Typography sx={{ fontWeight: fontWeight('medium') }}>
          Downstream impacted assets
        </Typography>
        <Typography
          sx={{ fontSize: fontSize('sm'), color: 'content.secondary' }}
        >
          {descriptionText}
        </Typography>
      </FlexColumn>

      <Controller
        name="assetFilters"
        control={methods.control}
        render={({ field }) => (
          <RuleFilterSelector
            field={field}
            methods={methods}
            type="assetFilters"
            disabled={disabled}
            filterOptions={filterOptions}
            defaultOperators={defaultOperators}
          />
        )}
      />

      <RuleFilterChips type="assetFilters" filterOptions={filterOptions} />
    </>
  );
};

export default ImpactedAssetsFiltersFormContent;
